import {
  type ComponentType,
  createContext,
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useStore } from 'zustand';

import { styled } from '@mui/material';
import { ViewModeProvider } from '@kc-console/context';

import { PanelErrorBoundary } from '../PanelErrorBoundary';

import type {
  SplitDetailPanelContextValue,
  TabItem,
  TabKey,
} from '@kc-console/service-modules/shared/split-panel';

type SplitPanelModule = typeof import('@kc-console/service-modules/shared/split-panel');

/**
 * SplitDetailPanelProvider가 없을 때도 hook 호출 순서를 유지하기 위한 더미 컨텍스트입니다.
 */
const NullSplitDetailPanelStoreContext = createContext<SplitDetailPanelContextValue | null>(null);

let cachedSplitPanelModule: SplitPanelModule | null = null;
let splitPanelModulePromise: Promise<SplitPanelModule | null> | null = null;

/**
 * Split panel 모듈을 동적 import로 안전하게 로드합니다.
 * @description
 * - require 사용 시 번들 환경에 따라 실패할 수 있는 문제를 방지하기 위해 dynamic import + 캐시를 사용합니다.
 * - 모듈 로딩 전에는 null을 반환하며, 로딩 완료 시 리렌더링으로 값을 제공합니다.
 * @example
 * const splitPanelModule = useSplitPanelModule();
 */
const useSplitPanelModule = () => {
  const [module, setModule] = useState<SplitPanelModule | null>(cachedSplitPanelModule);

  useEffect(() => {
    let mounted = true;

    if (!splitPanelModulePromise) {
      splitPanelModulePromise = import('@kc-console/service-modules/shared/split-panel')
        .then(loadedModule => {
          cachedSplitPanelModule = loadedModule;
          return loadedModule;
        })
        .catch(() => null);
    }

    splitPanelModulePromise.then(loadedModule => {
      if (mounted) {
        setModule(loadedModule);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  return module;
};

interface PanelTabHostProps {
  /** 패널 닫기 핸들러 (모든 탭 닫힐 때 호출) */
  onClosePanel?: () => void;
}

interface PanelTabHostInternalProps extends PanelTabHostProps {
  context: SplitDetailPanelContextValue;
  panelTabBar: ComponentType<any> | null;
}

/**
 * Internal component that uses the context
 */
const PanelTabHostInternal = ({
  context,
  onClosePanel,
  panelTabBar,
}: PanelTabHostInternalProps) => {
  const store = context.store;
  const maxTabs = context.maxTabs;

  // 스토어 상태 구독 (Zustand 공식 권장: useStore 사용)
  const tabs = useStore(store, state => state.tabs);
  const activeTabKey = useStore(store, state => state.activeTabKey);
  const switchTab = useStore(store, state => state.switchTab);
  const closeTab = useStore(store, state => state.closeTab);
  const closeAllTabs = useStore(store, state => state.closeAllTabs);

  // 탭 바에 전달할 탭 목록
  const tabBarTabs = useMemo(() => {
    return tabs.map((tab: TabItem) => ({
      key: tab.key,
      label: tab.label || tab.resourceId,
      closable: true,
    }));
  }, [tabs]);

  // 활성 탭 찾기
  const activeTab = useMemo(() => {
    return tabs.find((tab: TabItem) => tab.key === activeTabKey);
  }, [tabs, activeTabKey]);

  // 탭 바 표시 조건: maxTabs > 1이면 탭 수와 관계없이 표시
  const showTabBar = maxTabs > 1;

  // 탭 변경 핸들러
  const handleTabChange = (key: string) => {
    switchTab(key as TabKey);
  };

  // 탭 닫기 핸들러
  const handleTabClose = (key: string) => {
    closeTab(key as TabKey);

    // 마지막 탭이 닫히면 패널도 닫기
    if (tabs.length === 1) {
      onClosePanel?.();
    }
  };

  // 모든 탭 닫기 핸들러
  const handleCloseAll = () => {
    closeAllTabs();
    onClosePanel?.();
  };

  // 탭이 없으면 null 반환
  if (tabs.length === 0 || !activeTab) {
    return null;
  }

  // 활성 탭의 컴포넌트 렌더링
  const ActiveComponent = activeTab.component;
  const PanelTabBarComponent = panelTabBar;

  return (
    <PanelTabHostRoot>
      {showTabBar && PanelTabBarComponent && (
        <PanelTabBarComponent
          tabs={tabBarTabs}
          activeKey={activeTabKey}
          onTabChange={handleTabChange}
          onTabClose={handleTabClose}
          onCloseAll={handleCloseAll}
        />
      )}
      {/* Phase 7: 탭 패널 내부는 자동으로 embedded 모드 적용 */}
      <PanelTabContent>
        <ViewModeProvider viewMode="embedded">
          {/* 탭 콘텐츠(DetailContentGate 패널 종착 등)의 throw를 잡아 인플레이스 에러 화면으로 교체한다. */}
          {/* key(활성 탭 키)로 탭 전환 시 boundary를 remount하여 이전 탭의 에러 상태가 새 탭에 잔존하지 않게 한다. */}
          {/* resetKey는 생략한다: key와 같은 값을 넘기면 key가 같을 땐 resetKey도 같아 componentDidUpdate 리셋이 */}
          {/* 트리거되지 않고, key가 바뀌면 remount가 먼저 일어나 resetKey 비교가 무의미하므로 key 단독으로 충분하다. */}
          <PanelErrorBoundary
            key={String(activeTab.key)}
            onClose={() => handleTabClose(activeTab.key)}
          >
            <ActiveComponent item={activeTab.item} onClose={() => handleTabClose(activeTab.key)} />
          </PanelErrorBoundary>
        </ViewModeProvider>
      </PanelTabContent>
    </PanelTabHostRoot>
  );
};

/**
 * 탭 호스트 컴포넌트
 *
 * SplitDetailPanelProvider가 없으면 null을 반환합니다.
 */
const PanelTabHost = (props: PanelTabHostProps): ReactElement | null => {
  const splitPanelModule = useSplitPanelModule();
  const Context =
    splitPanelModule?.SplitDetailPanelStoreContext ?? NullSplitDetailPanelStoreContext;
  const PanelTabBarComponent = splitPanelModule?.PanelTabBar ?? null;

  const context = useContext(Context);

  // Provider가 없으면 null 반환
  if (!splitPanelModule || !context) {
    return null;
  }

  return <PanelTabHostInternal context={context} panelTabBar={PanelTabBarComponent} {...props} />;
};

export default PanelTabHost;

interface EmptyStoreState {
  activeTabKey: null;
  tabs: never[];
  visible: false;
}

/**
 * Empty store fallback for when SplitDetailPanelProvider is not available.
 * This allows hooks to be called unconditionally while still being reactive.
 */
const emptyStore = {
  getState: (): EmptyStoreState => ({ tabs: [], activeTabKey: null, visible: false }),
  subscribe: (): (() => void) => () => {
    // noop
  },
  setState: (_partial: Partial<EmptyStoreState>): void => {
    // noop
  },
  getInitialState: (): EmptyStoreState => ({ tabs: [], activeTabKey: null, visible: false }),
  destroy: (): void => {
    // noop
  },
};

/**
 * V2 패널 시스템(SplitDetailPanelProvider)을 사용하는지 확인하는 훅
 *
 * @returns SplitDetailPanelProvider가 있으면 true
 */
export const useIsV2PanelSystem = (): boolean => {
  const splitPanelModule = useSplitPanelModule();
  const Context =
    splitPanelModule?.SplitDetailPanelStoreContext ?? NullSplitDetailPanelStoreContext;
  const context = useContext(Context);

  return Boolean(splitPanelModule && context);
};

/**
 * 탭 바가 활성화되어 닫기 버튼을 대체할 수 있는지 확인하는 훅
 *
 * 다음 조건을 모두 만족해야 true 반환:
 * 1. SplitDetailPanelProvider가 있음
 * 2. maxTabs > 1 (탭 바가 표시되는 조건)
 * 3. 탭이 1개 이상 있음
 *
 * @note maxTabs=1인 경우 탭 바가 표시되지 않으므로 false 반환
 *       이 경우 PanelHeader의 닫기 버튼이 필요함
 */
export const usePanelTabHostAvailable = (): boolean => {
  const splitPanelModule = useSplitPanelModule();
  const Context =
    splitPanelModule?.SplitDetailPanelStoreContext ?? NullSplitDetailPanelStoreContext;
  const context = useContext(Context);

  // context가 없으면 emptyStore 사용 (hooks 조건부 호출 방지)
  const store = context?.store ?? emptyStore;

  // reactive하게 탭 개수 구독 (Zustand 공식 권장: useStore 사용)
  const tabCount = useStore(
    store,
    (state: { tabs?: { length: number } }) => state.tabs?.length ?? 0,
  );

  if (!splitPanelModule || !context) {
    return false;
  }

  // 탭 바 표시 조건과 동일하게 maxTabs > 1 확인
  // maxTabs=1이면 탭 바가 표시되지 않으므로 닫기 버튼이 필요
  const maxTabs = context.maxTabs;
  const showTabBar = maxTabs > 1;

  return showTabBar && tabCount > 0;
};

const PanelTabHostRoot = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const PanelTabContent = styled('div')`
  flex: 1;
  min-width: 0;
  overflow: auto;
`;
